/**
 * Hook for fetching historical performance data.
 * Supports time range filtering and transforms data for chart consumption.
 */

import useSWR from 'swr';
import { fetchPerformance, type PerformanceDataPoint } from '@/services/api';

export type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

interface UsePerformanceOptions {
  /** Time range for performance data */
  timeRange?: TimeRange;
  /** Refresh interval in milliseconds (default: 300000 - 5 minutes) */
  refreshInterval?: number;
}

/**
 * Convert time range to number of days.
 */
function timeRangeToDays(range: TimeRange): number {
  switch (range) {
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
    case '1y':
      return 365;
    case 'all':
      return 365 * 5; // 5 years max
    default:
      return 30;
  }
}

/**
 * Fetch historical performance data for an account.
 * 
 * @param accountId - The account ID to fetch performance for
 * @param options - Configuration options
 * @returns Performance data, loading state, error, and refresh function
 */
export function usePerformance(
  accountId: string | null,
  options: UsePerformanceOptions = {}
) {
  const { timeRange = '30d', refreshInterval = 300000 } = options;
  const days = timeRangeToDays(timeRange);

  const { data, error, isLoading, mutate } = useSWR(
    accountId ? ['performance', accountId, days] : null,
    () => (accountId ? fetchPerformance(accountId, days) : []),
    {
      refreshInterval,
      revalidateOnFocus: false,
      dedupingInterval: 60000, // Cache for 60 seconds
    }
  );

  // Transform data for chart consumption
  const chartData = (data ?? []).map((point) => ({
    date: point.date,
    value: point.value,
    pnl: point.pnl,
    // Format date for display
    label: formatDateLabel(point.date, timeRange),
  }));

  // Calculate summary statistics
  const stats = calculatePerformanceStats(data ?? []);

  return {
    data: chartData,
    stats,
    error: error?.message ?? null,
    isLoading,
    refresh: mutate,
  };
}

/**
 * Format date label based on time range.
 */
function formatDateLabel(dateStr: string, range: TimeRange): string {
  const date = new Date(dateStr);
  
  if (range === '7d') {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (range === '30d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (range === '90d' || range === '1y') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

/**
 * Calculate performance statistics from data points.
 */
function calculatePerformanceStats(data: PerformanceDataPoint[]) {
  if (data.length === 0) {
    return {
      startValue: 0,
      endValue: 0,
      totalReturn: 0,
      totalReturnPercent: 0,
      maxValue: 0,
      minValue: 0,
      avgDailyPnl: 0,
    };
  }

  const firstPoint = data[0];
  const lastPoint = data[data.length - 1];
  const startValue = firstPoint?.value ?? 0;
  const endValue = lastPoint?.value ?? 0;
  const totalReturn = endValue - startValue;
  const totalReturnPercent = startValue > 0 ? (totalReturn / startValue) * 100 : 0;

  const values = data.map((d) => d.value);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);

  const totalPnl = data.reduce((sum, d) => sum + d.pnl, 0);
  const avgDailyPnl = totalPnl / data.length;

  return {
    startValue,
    endValue,
    totalReturn,
    totalReturnPercent,
    maxValue,
    minValue,
    avgDailyPnl,
  };
}

/**
 * Available time range options for UI.
 */
export const TIME_RANGE_OPTIONS: Array<{ value: TimeRange; label: string }> = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '1y', label: '1 Year' },
  { value: 'all', label: 'All Time' },
];
